import React, { useContext, useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MyContext } from "../Context/MyContext";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { FormPageSkeleton } from "../Components/Skeleton/Skeleton";
import { calculateEmi, clamp } from "../../Utils/emiCalculator";
import "./EmiCalculator.css";

const formatCurrency = (symbol, value) =>
  `${symbol}${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;

export default function EmiCalculator() {
  const { emi_page: page } = useContext(MyContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const calc = page.calculator || {};
  const resultConfig = page.result || {};
  const breakdown = page.breakdown || {};
  const actions = page.actions || {};
  const symbol = calc.currencySymbol || "₹";

  const priceFromUrl = Number(searchParams.get("price")) || 0;

  const [carPrice, setCarPrice] = useState(
    priceFromUrl || calc.defaultCarPrice || 800000
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(
    calc.defaultDownPaymentPercent ?? 20
  );
  const [interestRate, setInterestRate] = useState(calc.defaultInterestRate ?? 10.5);
  const [tenureMonths, setTenureMonths] = useState(calc.defaultTenureMonths ?? 60);

  useEffect(() => {
    if (priceFromUrl > 0) setCarPrice(priceFromUrl);
  }, [priceFromUrl]);

  const downPayment = Math.round((carPrice * downPaymentPercent) / 100);
  const loanAmount = Math.max(0, carPrice - downPayment);

  const { emi, totalInterest, totalPayable } = useMemo(
    () => calculateEmi(loanAmount, interestRate, tenureMonths),
    [loanAmount, interestRate, tenureMonths]
  );

  const principalShare = totalPayable > 0 ? (loanAmount / totalPayable) * 100 : 50;
  const interestShare = 100 - principalShare;

  const applyTenure = (months) => {
    setTenureMonths(clamp(months, calc.minTenureMonths ?? 12, calc.maxTenureMonths ?? 84));
  };

  return (
    <PageLoadGate skeleton={<FormPageSkeleton />} delay={380}>
      <div className="emi-page">
        <div className="emi-hero">
          <div className="emi-hero-inner">
            <span className="emi-badge">{page.badge}</span>
            <h1>{page.title}</h1>
            <p>{page.subtitle}</p>
          </div>
        </div>

        <div className="emi-body">
          <div className="emi-layout">
            <div className="emi-card emi-calculator-card">
              <div className="emi-sliders">
                <div className="emi-field">
                  <div className="emi-field-head">
                    <label>{page.fields?.[0]?.label || "Car Price"}</label>
                    <strong>{formatCurrency(symbol, carPrice)}</strong>
                  </div>
                  <input
                    type="range"
                    min={calc.minCarPrice ?? 50000}
                    max={calc.maxCarPrice ?? 50000000}
                    step={10000}
                    value={carPrice}
                    onChange={(e) => setCarPrice(Number(e.target.value))}
                  />
                  {page.fields?.[0]?.hint && <span className="emi-hint">{page.fields[0].hint}</span>}
                </div>

                <div className="emi-field">
                  <div className="emi-field-head">
                    <label>{page.fields?.[1]?.label || "Down Payment"}</label>
                    <strong>
                      {downPaymentPercent}% ({formatCurrency(symbol, downPayment)})
                    </strong>
                  </div>
                  <input
                    type="range"
                    min={calc.minDownPaymentPercent ?? 0}
                    max={calc.maxDownPaymentPercent ?? 80}
                    step={5}
                    value={downPaymentPercent}
                    onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                  />
                  {page.fields?.[1]?.hint && <span className="emi-hint">{page.fields[1].hint}</span>}
                </div>

                <div className="emi-field">
                  <div className="emi-field-head">
                    <label>{page.fields?.[2]?.label || "Interest Rate"}</label>
                    <strong>{interestRate}% p.a.</strong>
                  </div>
                  <input
                    type="range"
                    min={calc.minInterestRate ?? 5}
                    max={calc.maxInterestRate ?? 24}
                    step={0.25}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                  />
                  {page.fields?.[2]?.hint && <span className="emi-hint">{page.fields[2].hint}</span>}
                </div>

                <div className="emi-field">
                  <div className="emi-field-head">
                    <label>{page.fields?.[3]?.label || "Loan Tenure"}</label>
                    <strong>
                      {tenureMonths} months ({Math.round(tenureMonths / 12)} yr)
                    </strong>
                  </div>
                  <input
                    type="range"
                    min={calc.minTenureMonths ?? 12}
                    max={calc.maxTenureMonths ?? 84}
                    step={calc.tenureStep ?? 6}
                    value={tenureMonths}
                    onChange={(e) => setTenureMonths(Number(e.target.value))}
                  />
                  <div className="emi-tenure-chips">
                    {(page.tenureOptions || []).map((opt) => (
                      <button
                        key={opt.months}
                        type="button"
                        className={tenureMonths === opt.months ? "active" : ""}
                        onClick={() => applyTenure(opt.months)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="emi-result-panel">
                <span className="emi-result-label">{resultConfig.emiLabel}</span>
                <div className="emi-result-value">
                  {formatCurrency(symbol, emi)}
                  <small>{resultConfig.perMonthSuffix}</small>
                </div>

                <div className="emi-result-grid">
                  <div>
                    <span>{resultConfig.loanAmountLabel}</span>
                    <strong>{formatCurrency(symbol, loanAmount)}</strong>
                  </div>
                  <div>
                    <span>{resultConfig.downPaymentLabel}</span>
                    <strong>{formatCurrency(symbol, downPayment)}</strong>
                  </div>
                  <div>
                    <span>{resultConfig.totalInterestLabel}</span>
                    <strong>{formatCurrency(symbol, totalInterest)}</strong>
                  </div>
                  <div>
                    <span>{resultConfig.totalPayableLabel}</span>
                    <strong>{formatCurrency(symbol, totalPayable)}</strong>
                  </div>
                </div>

                <div className="emi-breakdown">
                  <h4>{breakdown.title}</h4>
                  <div className="emi-breakdown-bar">
                    <span style={{ width: `${principalShare}%` }} title={breakdown.principalLabel} />
                    <span style={{ width: `${interestShare}%` }} title={breakdown.interestLabel} />
                  </div>
                  <div className="emi-breakdown-legend">
                    <span>
                      <i className="principal" /> {breakdown.principalLabel} ({Math.round(principalShare)}%)
                    </span>
                    <span>
                      <i className="interest" /> {breakdown.interestLabel} ({Math.round(interestShare)}%)
                    </span>
                  </div>
                </div>

                <p className="emi-disclaimer">{resultConfig.disclaimer}</p>

                <div className="emi-actions">
                  <button
                    type="button"
                    className="emi-btn primary"
                    onClick={() => navigate(actions.contactPath || "/contact")}
                  >
                    {actions.contactLabel}
                  </button>
                  <button
                    type="button"
                    className="emi-btn secondary"
                    onClick={() => navigate(actions.browsePath || "/cars")}
                  >
                    {actions.browseLabel}
                  </button>
                  <Link className="emi-btn ghost" to={actions.valuationPath || "/valuation"}>
                    {actions.valuationLabel}
                  </Link>
                </div>
              </div>
            </div>

            <aside className="emi-sidebar">
              {(page.partnerPlans?.items || []).length > 0 && (
                <div className="emi-partner-card">
                  <h3>{page.partnerPlans.title}</h3>
                  <p>{page.partnerPlans.subtitle}</p>
                  <ul>
                    {page.partnerPlans.items.map((plan) => {
                      const planEmi = calculateEmi(
                        loanAmount,
                        plan.rate,
                        plan.tenureMonths
                      ).emi;
                      return (
                        <li key={plan.id}>
                          <div>
                            <strong>{plan.name}</strong>
                            <span>{plan.highlight}</span>
                          </div>
                          <div>
                            <small>
                              {plan.rate}% · {plan.tenureMonths} mo
                            </small>
                            <strong>{formatCurrency(symbol, planEmi)}/mo</strong>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {(page.highlights || []).map((item) => (
                <div key={item.id} className="emi-highlight">
                  <span>{item.icon}</span>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </aside>
          </div>
        </div>
      </div>
    </PageLoadGate>
  );
}
